import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPlatformStatus, type StatusRow } from "@/lib/platform-status.functions";

export const Route = createFileRoute("/status")({
  head: () => ({
    meta: [
      { title: "Platform status — The Hospitality Reserve" },
      { name: "description", content: "Live status of The Hospitality Reserve marketplace, payouts, reminders and background jobs." },
      { property: "og:title", content: "Platform status — The Hospitality Reserve" },
      { property: "og:url", content: "https://thehospitalityreserve.com/status" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/status" }],
  }),
  component: StatusPage,
});

const dot = {
  operational: "bg-emerald-500",
  degraded: "bg-amber-500",
  down: "bg-destructive",
  unknown: "bg-muted-foreground",
} as const;

const overallCopy = {
  operational: "All systems operational",
  degraded: "Some systems degraded",
  down: "Outage in progress",
} as const;

function StatusPage() {
  const fetchStatus = useServerFn(getPlatformStatus);
  const { data } = useQuery({
    queryKey: ["platform-status"],
    queryFn: () => fetchStatus({}),
    refetchInterval: 60_000,
  });

  return (
    <main className="mx-auto max-w-3xl space-y-8 p-6">
      <header>
        <p className="text-[0.6rem] tracking-wide-luxury uppercase text-accent">Live status</p>
        <h1 className="text-3xl font-medium">The Hospitality Reserve</h1>
        {data && (
          <div className="mt-4 flex items-center gap-3 border border-border p-4">
            <span className={`inline-block size-3 rounded-full ${dot[data.overall]}`} />
            <p className="text-sm font-medium">{overallCopy[data.overall]}</p>
            <p className="ml-auto text-xs text-muted-foreground">
              Updated {new Date(data.generated_at).toLocaleTimeString("en-GB")}
            </p>
          </div>
        )}
      </header>

      <section className="border border-border">
        <ul>
          {(data?.hooks ?? []).map((h: StatusRow) => (
            <li key={h.name} className="flex items-center gap-3 border-b border-border p-4 last:border-b-0">
              <span className={`inline-block size-2.5 rounded-full ${dot[h.state]}`} />
              <div className="flex-1">
                <p className="text-sm font-medium">{h.label}</p>
                <p className="text-xs text-muted-foreground">
                  {h.last_run_at ? `Last check ${new Date(h.last_run_at).toLocaleString("en-GB")}` : "No recent activity"}
                  {h.success_rate_24h !== null && ` · ${h.success_rate_24h}% success (24h)`}
                </p>
              </div>
              <span className="text-xs uppercase tracking-wide-luxury text-muted-foreground">{h.state}</span>
            </li>
          ))}
        </ul>
      </section>

      <p className="text-xs text-muted-foreground">
        This page auto-refreshes every minute. If something looks wrong, email{" "}
        <a className="underline" href="mailto:support@thehospitalityreserve.com">support@thehospitalityreserve.com</a>.
      </p>
    </main>
  );
}
